// jan27 Signup.jsx, updated to move heavy logic into SignupLoadingPage
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Alert,
  Divider,
  Grid,
  List,
  ListItem,
  Tooltip,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  FormControl, 
  FormHelperText
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useLoading } from '../contexts/LoadingContext'; // Correctly using useLoading hook
import { useAuth } from '../contexts/AuthContext'; // Using AuthContext for auth state management
import { useOrganization2 } from '../contexts/OrganizationContext2'; // Import OrganizationContext2

// We'll keep your logs/comments, but note some steps were removed to the loading page

const passwordCriteriaText = {
  minLength: 'At least 8 characters long',
  hasUpperCase: 'Includes at least one Uppercase letter',
  hasLowerCase: 'Includes at least one lowercase letter',
  hasNumbers: 'Includes at least one number',
  hasSpecialChars: 'Includes a special character',
};

// List of blocked domains that are commonly used for spam, testing, or invalid emails
const blockedDomains = ['email.com', 'test.com', 'example.com', 'fake.com'];

const Signup = () => {
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true); // Track if the email is valid
  const [isEmailBlocked, setIsEmailBlocked] = useState(false); // Track if the email domain is blocked
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [showPasswordCriteria, setShowPasswordCriteria] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [showPassword, setShowPassword] = useState({ password: false, confirm: false });
  const [canCreateAccount, setCanCreateAccount] = useState(false); // Track if the form is valid to enable the "Create Account" button
  
  const navigate = useNavigate();
  const location = useLocation();

  const isTemplateSignup =
    new URLSearchParams(location.search).get('intent') === 'duplicate_template';

  const { isLoading, setLoading } = useLoading(); // Correctly using useLoading hook
  // AuthContext is still available if needed, but the heavy logic is now in SignupLoadingPage
  const { setAuthState } = useAuth(); 
  // OrganizationContext2 is no longer used here for creation – we do that in loading page
  const { createOrganization } = useOrganization2(); 
  const logoSrc = process.env.PUBLIC_URL + '/utmlogo.svg';
  const [tosPrivacyChecked, setTosPrivacyChecked] = useState(false);

  // ─────────────────────────────────────────────────────────────
  // Landing-page intent capture (template duplication bootstrap)
  // Example:
  //   /signup?intent=duplicate_template&templateKey=adobe_cid_table&source=adobe_tool
  //
  // We only store lightweight intent flags here.
  // The actual duplication happens later in SignupLoadingPage after:
  //   - org creation
  //   - Firestore profile write
  //   - AuthContext forceRefresh
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);

      const intent = params.get('intent');
      const templateKey = params.get('templateKey');
      const source = params.get('source');

      if (intent) {
        sessionStorage.setItem('signupIntent', intent);
        console.log('[Signup] Captured intent from URL params:', intent);
      }

      if (templateKey) {
        sessionStorage.setItem('signupTemplateKey', templateKey);
        console.log('[Signup] Captured templateKey from URL params:', templateKey);
      }

      if (source) {
        sessionStorage.setItem('signupSource', source);
        console.log('[Signup] Captured source from URL params:', source);
      }
    } catch (e) {
      console.warn('[Signup] Failed to capture intent params (non-fatal):', e?.message || e);
    }
  }, [location.search]);

  // Validate password function remains
  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('minLength');
    if (!/[A-Z]/.test(password)) errors.push('hasUpperCase');
    if (!/[a-z]/.test(password)) errors.push('hasLowerCase');
    if (!/\d/.test(password)) errors.push('hasNumbers');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('hasSpecialChars');
    return errors;
  };

  // Handle password changes
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setShowPasswordCriteria(newPassword.length > 0);

    const errors = validatePassword(newPassword);
    setPasswordErrors(errors);
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Email validation using a regular expression
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Standard email format validation
    return emailRegex.test(email);
  };

  // Check if the email contains a domain from the blocked list
  const isEmailDomainBlocked = (email) => {
    const domain = email.split('@')[1]; // Get the domain from the email
    return blockedDomains.includes(domain);
  };

  // Handle email change and validation
  const handleEmailChange = (e) => {
    const inputEmail = e.target.value;
    setEmail(inputEmail);
    const isValid = validateEmail(inputEmail);
    const isBlocked = isEmailDomainBlocked(inputEmail);
    setIsEmailValid(isValid); // Set validity based on regex match
    setIsEmailBlocked(isBlocked); // Check if the domain is blocked
  };

  // Update the state for "Confirm Password" and error handling for password mismatch
  useEffect(() => {
    const passwordsMatch = password === confirmPassword;
    const isPasswordValid = passwordErrors.length === 0 && password !== '';
    const isFormValid =
      isPasswordValid &&
      passwordsMatch &&
      email !== '' &&
      orgName !== '' &&
      isEmailValid &&
      !isEmailBlocked &&
      tosPrivacyChecked;

    setPasswordError(!passwordsMatch);
    setCanCreateAccount(isFormValid); // Enable button when all fields are valid, including email
  }, [
    password,
    confirmPassword,
    passwordErrors,
    email,
    orgName,
    isEmailValid,
    isEmailBlocked,
    tosPrivacyChecked
  ]);

  // handleSubmit now ONLY creates the Firebase user, then navigates to /loadingsignup
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true, 'Signup');

    try {
      console.log('--- Begin handleSubmit ---');
      console.log('Email:', email);
      console.log('Organization Name:', orgName);

      // Clear any existing error messages
      setError('');

      // Check if TOS/Privacy box is checked.
      // If not, set an error and return immediately.
      if (!tosPrivacyChecked) {
        setError('You must agree to our terms of service and privacy policy.');
        setLoading(false, 'Signup');
        return;
      }

      // Step 1 (Only step here): Create a user with Firebase Authentication
      console.log('Creating user with Firebase Authentication...');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log('User created successfully with ID:', user.uid);

      // Create a minimal accounts/{uid} doc immediately so AuthContext profile fetch does not fail.
      // Use merge:true so the loading page can add organizationId and other fields later.
      const userDocRef = doc(db, 'accounts', user.uid);
      await setDoc(
        userDocRef,
        {
          email,
          organizationId: null,
          role: 'owner', // initial role for org creator
        },
        { merge: true }
      );

      // Store minimal data in sessionStorage for SignupLoadingPage
      sessionStorage.setItem('signupUserUid', user.uid);

      sessionStorage.setItem('signupUserEmail', email);
      sessionStorage.setItem('signupOrgName', orgName);
      sessionStorage.setItem(
        'signupTosAccepted',
        tosPrivacyChecked ? 'true' : 'false'
      );
      
      // Navigate to SignupLoadingPage (the new route that does Steps 2–6)
      console.log('Redirecting to SignupLoadingPage immediately...');
      navigate('/loadingsignup');
    } catch (error) {
      console.error('Signup Error:', error.message || error);
      setError(`Failed to create an account: ${error.message || error}`);
    } finally {
      // Always reset the loading state
      setLoading(false, 'Signup');
      console.log('--- End handleSubmit ---');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'stretch',
        backgroundColor: '#f5f5f5',
      }}
    >
      <Grid container sx={{ width: '100%' }}>
        {/* LEFT SIDE: brand + value props (NOT inside a card) */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'center', md: 'center' },
            px: { xs: 3, sm: 6, md: 8 },
            py: { xs: 4, md: 0 },

            // Option A + tint:
            // - Solid, subtle brand tint (no gradient)
            // - Thin brand stripe for a premium enterprise feel
            backgroundColor: 'rgba(72, 157, 255, 0.04)',
            position: 'relative',

            borderRight: { md: '1px solid rgba(0,0,0,0.08)' },

            // Thin brand stripe
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '6px',
              backgroundColor: '#489DFF',
            },
          }}

        >
          {/* Only the logo is centered; all text is left-aligned (as requested). */}
          <Box sx={{ width: '100%', maxWidth: 520 }}>
            {/* ✅ Center ONLY the logo */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <img
                src={logoSrc}
                alt="Logo"
                style={{ maxWidth: '160px' }}
              />
            </Box>

            {/* ✅ Left-align everything else */}
<Box sx={{ textAlign: 'left' }}>
  <Typography
    variant="h4"
    sx={{
      fontWeight: 900,
      letterSpacing: '-0.02em',
      mb: 1,
      lineHeight: 1.1,
    }}
  >
    Welcome!
  </Typography>

  <Typography
    variant="h6"
    sx={{
      color: 'text.primary',
      fontWeight: 500,
      mb: 2.25,
      lineHeight: 1.35,
      maxWidth: 520,
    }}
  >
    {isTemplateSignup
      ? 'Make this template yours. Fully editable and ready to launch.'
      : 'Build governed campaign URLs at scale.'}
  </Typography>

  {/* Indented list, tighter rhythm, no checkmarks */}
  <Box
    sx={{
      display: 'grid',
      gap: 0.75,     // tighter gap between the 3 lines
      mb: 2.25,
      pl: 2,         // indentation
      maxWidth: 520,
    }}
  >
    {isTemplateSignup ? (
      <>
        <Typography
          variant="body1"
          sx={{
            color: 'text.primary',
            fontWeight: 500,
            lineHeight: 1.6,
          }}
        >
          Customize design, fields, and dropdown rules
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'text.primary',
            fontWeight: 500,
            lineHeight: 1.6,
          }}
        >
          Align every campaign to your standards
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'text.primary',
            fontWeight: 500,
            lineHeight: 1.6,
          }}
        >
          Go live in minutes
        </Typography>
      </>
    ) : (
      <>
        <Typography
          variant="body1"
          sx={{
            color: 'text.primary',
            fontWeight: 500,
            lineHeight: 1.6,
          }}
        >
          Standardize your campaign tracking
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'text.primary',
            fontWeight: 500,
            lineHeight: 1.6,
          }}
        >
          Customize rules, dropdowns, and naming logic
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'text.primary',
            fontWeight: 500,
            lineHeight: 1.6,
          }}
        >
          Launch clean campaigns in minutes
        </Typography>
      </>
    )}
  </Box>

  <Typography
    variant="body2"
    sx={{
      color: 'text.secondary',
      fontWeight: 500,
      lineHeight: 1.6,
      maxWidth: 520,
    }}
  >
    {isTemplateSignup
      ? 'Start building free for 14 days. No credit card required.'
      : 'Start building free for 14 days. No credit card required.'}
  </Typography>
</Box>
          </Box>
        </Grid>

        {/* RIGHT SIDE: the signup form (card only on the right) */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2, sm: 4, md: 6 },
            py: { xs: 4, md: 0 },
          }}
        >
          <Paper
            elevation={3}
            sx={{
              width: '100%',
              maxWidth: 520,
              textAlign: 'center',
              p: { xs: 3, sm: 4 },

              // Keep it usable on shorter-height viewports.
              // On desktop, allow internal scrolling rather than pushing below the window.
              maxHeight: { xs: 'unset', md: 'calc(100vh - 64px)' },
              overflow: { md: 'auto' },
            }}
          >
            {/* ✅ Title/subtitle intentionally removed (left panel already describes purpose). */}

            {error && (
              <Alert severity="error" sx={{ marginBottom: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                label="Work Email"
                type="email"
                required
                fullWidth
                margin="normal"
                value={email}
                onChange={handleEmailChange}
                disabled={isLoading}
                error={!isEmailValid || isEmailBlocked} // Show error if email is invalid or blocked
                helperText={
                  !isEmailValid
                    ? 'Please enter a valid work email address.'
                    : isEmailBlocked
                    ? 'This email domain is not allowed.'
                    : ''
                }
              />

              <TextField
                label="Password"
                type={showPassword.password ? 'text' : 'password'}
                required
                fullWidth
                margin="normal"
                value={password}
                onChange={handlePasswordChange}
                disabled={isLoading}
                error={passwordErrors.length > 0}
                helperText={
                  passwordErrors.length > 0 ? 'Password does not meet criteria' : ''
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => togglePasswordVisibility('password')}
                        edge="end"
                      >
                        {showPassword.password ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onFocus={() => setShowPasswordCriteria(true)}
                onBlur={() => setShowPasswordCriteria(false)}
              />

              {/* Show password criteria if user is typing */}
              {showPasswordCriteria && (
                <List dense sx={{ maxWidth: 360, margin: 'auto', mb: 1 }}>
                  {Object.keys(passwordCriteriaText).map((criteriaKey) => (
                    <Tooltip title={passwordCriteriaText[criteriaKey]} key={criteriaKey}>
                      <ListItem sx={{ py: 0.5 }}>
                        <FiberManualRecordIcon
                          fontSize="small"
                          sx={{
                            color: passwordErrors.includes(criteriaKey)
                              ? 'error.main'
                              : 'success.main',
                            fontSize: '10px',
                          }}
                        />
                        <Typography variant="body2" sx={{ ml: 1, fontSize: '12px' }}>
                          {passwordCriteriaText[criteriaKey]}
                        </Typography>
                      </ListItem>
                    </Tooltip>
                  ))}
                </List>
              )}

              <TextField
                label="Confirm Password"
                type={showPassword.confirm ? 'text' : 'password'}
                required
                fullWidth
                margin="normal"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={passwordErrors.length > 0 || !password}
                error={passwordError}
                helperText={passwordError ? 'Passwords do not match.' : ''}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => togglePasswordVisibility('confirm')}
                        edge="end"
                      >
                        {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Organization Name"
                type="text"
                required
                fullWidth
                margin="normal"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                disabled={isLoading}
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isLoading || !canCreateAccount}
                sx={{ marginTop: 2, mb: 1 }}
              >
                Start Free Trial
              </Button>

              {/* 
                 Wrapping the checkbox in a FormControl so we can display an error 
                 if the user doesn't check the box. 
              */}
              <FormControl
                required
                error={
                  !tosPrivacyChecked &&
                  error === 'You must agree to our terms of service and privacy policy.'
                }
                sx={{
                  mb: 2,
                  // Hide the asterisk while still respecting "required" logic
                  '& .MuiFormLabel-asterisk': {
                    display: 'none !important',
                  },
                  // Also hide any leftover asterisk from form control labels
                  '& .MuiFormControlLabel-asterisk': {
                    display: 'none !important',
                  },
                }}
              >
                <FormControlLabel
                  // Keep the label after the checkbox
                  labelPlacement="end"
                  // This ensures the label/checkbox themselves are arranged to the left
                  sx={{
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                  }}
                  control={
                    <Checkbox
                      checked={tosPrivacyChecked}
                      onChange={(e) => setTosPrivacyChecked(e.target.checked)}
                      color="primary"
                      required
                      disabled={isLoading}
                      // Reduce checkbox icon size
                      sx={{
                        '& .MuiSvgIcon-root': {
                          fontSize: 18, // Adjust as needed; default ~24
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'gray',
                        fontSize: '0.85rem',
                        textAlign: 'left', // Left-align the text
                      }}
                    >
                      By signing up, you agree to UTM Smart Manager&apos;s{' '}
                      <Link
                        to="https://app.utmsmartmanager.com/terms-and-conditions"
                        style={{ textDecoration: 'none', color: '#1976d2' }}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Service
                      </Link>
                      ,{' '}
                      <Link
                        to="https://app.utmsmartmanager.com/privacy-policy"
                        style={{ textDecoration: 'none', color: '#1976d2' }}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </Link>
                      , and occasional update emails.
                    </Typography>
                  }
                />
                {(!tosPrivacyChecked &&
                  error === 'You must agree to our terms of service and privacy policy.') && (
                  <FormHelperText error>
                    {error}
                  </FormHelperText>
                )}
              </FormControl>
            </form>

            <Divider sx={{ my: 2 }} />
            <Grid container justifyContent="center">
              <Typography variant="body2">
                Already have an account?{' '}
                <Link to="/login" style={{ textDecoration: 'none', ml: 1 }}>
                  Login
                </Link>
              </Typography>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Signup;
